import { CallDto, CallUser } from '@act/shared/data-transfer-objects';
import {
  Button,
  FlexContainer,
  IconButton,
  IconLibrary,
  ToggleSwitch,
} from '@act/storybook';
import { useToast } from 'react-components/app/ReusableToast';
import { TrackingProvider } from 'react-components/tracking';
import { AssignPhoneNumberFormComponent } from 'react-components/form-dialog/AssignPhoneNumberForm';
import { ResourceCallForm } from './ResourceCallForm';
import { ConnectionCard } from './ConnectionCard';
import { ActiveCallMenuAddParticipantForm } from './ActiveCallMenuAddParticipantForm';
import { AddCallerForm } from './useActiveCallAddParticipant.hook';
import { Control, FormState } from 'react-hook-form';
import { FormConfig } from 'stories/forms/FormBuilder/formBuilder.types';
import { SelectOption } from 'react-components/components/SearchBar';

export interface ActiveCallMenuAnsweredViewProps {
  call: CallDto;
  connections: CallDto['connections'];
  activeConnectionsCount: number;
  showConnectionBorders: boolean;
  verticalCards: boolean;
  isMainConnectionOnHold: boolean;
  isMemberKnown: boolean;
  shouldShowAddCallerButton: boolean;
  isAddingConnection: boolean;
  setIsAddingConnection: (open: boolean) => void;
  isAssigningNumber: boolean;
  setIsAssigningNumber: (open: boolean) => void;
  mainConnection: CallUser | undefined;
  resourcingCallsFF: boolean;
  selectedCallType: string;
  setSelectedCallType: (value: string) => void;
  addParticipant: {
    control: Control<AddCallerForm>;
    formState: FormState<AddCallerForm>;
    isValid: boolean;
    guideId: string | null;
    addCallerFormConfig: FormConfig<AddCallerForm>;
    memberOptions: SelectOption[];
    isLoadingMembers: boolean;
    isLoadingTypeaheadMembers: boolean;
    selectedMemberOption: SelectOption | null;
    setSelectedMemberOption: (option: SelectOption | null) => void;
    setTypeaheadSearchTerm: (term: string) => void;
  };
  isAddingGuideToCall: boolean;
  isMergingCalls: boolean;
  isHangingUpGuide: boolean;
  guideConnection: CallDto['connections'][0] | null | undefined;
  onMergeCall: () => void;
  onLeaveCall: () => void;
  onSubmitAddCaller: () => void;
}

/**
 * Full call controls after the guide has answered their phone.
 *
 * @param props Layout and handler props from ActiveCallMenu.
 * @returns Answered-state call menu UI.
 */
export const ActiveCallMenuAnsweredView = ({
  call,
  connections,
  activeConnectionsCount,
  showConnectionBorders,
  verticalCards,
  isMainConnectionOnHold,
  isMemberKnown,
  shouldShowAddCallerButton,
  isAddingConnection,
  setIsAddingConnection,
  isAssigningNumber,
  setIsAssigningNumber,
  mainConnection,
  resourcingCallsFF,
  selectedCallType,
  setSelectedCallType,
  addParticipant,
  isAddingGuideToCall,
  isMergingCalls,
  isHangingUpGuide,
  guideConnection,
  onMergeCall,
  onLeaveCall,
  onSubmitAddCaller,
}: ActiveCallMenuAnsweredViewProps) => {
  const toast = useToast();

  const toggleAddConnection = () => {
    setIsAddingConnection(!isAddingConnection);
    if (!isAddingConnection) {
      setIsAssigningNumber(false);
    }
  };

  const toggleAssignNumber = () => {
    setIsAssigningNumber(!isAssigningNumber);
    if (!isAssigningNumber) {
      setIsAddingConnection(false);
    }
  };

  return (
    <>
      <FlexContainer
        direction="row"
        alignItems="center"
        gap={2}
        justifyContent="center"
        margin="0 0 24px 0"
        overflow="visible"
        overflowAllVisible
        height="auto"
      >
        {isMainConnectionOnHold && (
          <IconButton
            color="primary"
            size="large"
            onClick={onMergeCall}
            label="Merge"
            disabled={isMergingCalls}
            trackingName="merge-call-button"
          >
            <IconLibrary
              iconName="ArrowsLeftRight"
              weight="regular"
              size={18}
            />
          </IconButton>
        )}
        {!isMemberKnown && (
          <IconButton
            color="primary"
            size="large"
            active={isAssigningNumber}
            onClick={toggleAssignNumber}
            label="Assign number"
            trackingName="assign-phone-number-button"
          >
            <IconLibrary
              iconName="UserCirclePlus"
              weight="regular"
              size={22}
            />
          </IconButton>
        )}
        {!verticalCards && shouldShowAddCallerButton && (
          <IconButton
            color="primary"
            size="large"
            active={isAddingConnection}
            onClick={toggleAddConnection}
            label="Add caller"
            trackingName="add-caller-button"
          >
            <IconLibrary iconName="PhonePlus" weight="regular" size={22} />
          </IconButton>
        )}
      </FlexContainer>

      {connections.length > 0 && (
        <FlexContainer
          direction="row"
          gap={2}
          margin="0 0 24px 0"
          flexWrap
          overflow="visible"
          overflowAllVisible
          height="auto"
        >
          {connections.map(connection => (
            <ConnectionCard
              key={connection.id}
              connection={connection}
              activeConnectionsCount={activeConnectionsCount}
              showBorder={showConnectionBorders}
              verticalCards={verticalCards}
            />
          ))}
        </FlexContainer>
      )}

      {isAssigningNumber && mainConnection?.phoneNumber && (
        <TrackingProvider contextName="assign-phone-number-form">
          <FlexContainer
            direction="column"
            padding={2}
            backgroundColor="white"
            borderRadius="moreRounded"
            margin="0 0 24px 0"
            overflow="visible"
            overflowAllVisible
            height="auto"
          >
            <AssignPhoneNumberFormComponent
              phoneNumber={mainConnection.phoneNumber}
              callId={call.id}
              onSuccess={() => {
                setIsAssigningNumber(false);
                toast('Phone number assigned successfully!', 'success');
              }}
              onCancel={() => setIsAssigningNumber(false)}
              showActions
              showAlert
              showTitle
            />
          </FlexContainer>
        </TrackingProvider>
      )}

      {isAddingConnection && resourcingCallsFF && (
        <FlexContainer
          direction="row"
          justifyContent="center"
          overflow="visible"
          overflowAllVisible
          height="auto"
        >
          <ToggleSwitch
            onChange={setSelectedCallType}
            options={[
              { label: 'Internal call', value: 'internal' },
              { label: 'Resource call', value: 'resource' },
            ]}
            value={selectedCallType}
          />
        </FlexContainer>
      )}

      {isAddingConnection && selectedCallType === 'internal' && (
        <ActiveCallMenuAddParticipantForm
          isMemberKnown={isMemberKnown}
          isValid={addParticipant.isValid}
          guideId={addParticipant.guideId}
          control={addParticipant.control}
          formState={addParticipant.formState}
          addCallerFormConfig={addParticipant.addCallerFormConfig}
          memberOptions={addParticipant.memberOptions}
          isLoadingMembers={addParticipant.isLoadingMembers}
          isLoadingTypeaheadMembers={addParticipant.isLoadingTypeaheadMembers}
          selectedMemberOption={addParticipant.selectedMemberOption}
          setSelectedMemberOption={addParticipant.setSelectedMemberOption}
          setTypeaheadSearchTerm={addParticipant.setTypeaheadSearchTerm}
          isAddingGuideToCall={isAddingGuideToCall}
          onSubmit={onSubmitAddCaller}
        />
      )}

      {isAddingConnection && selectedCallType === 'resource' && (
        <TrackingProvider contextName="add-resource-form">
          <FlexContainer
            direction="column"
            padding={2}
            backgroundColor="white"
            borderRadius="moreRounded"
            margin="0 0 24px 0"
            overflow="visible"
            overflowAllVisible
            height="auto"
          >
            <ResourceCallForm
              callId={call.id}
              handleClose={() => setIsAddingConnection(false)}
            />
          </FlexContainer>
        </TrackingProvider>
      )}

      <FlexContainer
        direction="row"
        justifyContent="flex-end"
        margin="24px 0 0 0"
        gap={3}
        overflow="visible"
        overflowAllVisible
        height="auto"
      >
        {verticalCards && shouldShowAddCallerButton && (
          <IconButton
            color="primary"
            size="large"
            active={isAddingConnection}
            onClick={toggleAddConnection}
            trackingName="add-caller-button"
          >
            <IconLibrary iconName="PhonePlus" weight="regular" size={18} />
          </IconButton>
        )}
        <Button
          variant="outlined"
          color="error"
          onClick={onLeaveCall}
          disabled={isHangingUpGuide || !guideConnection}
          trackingName="leave-call-button"
          startIcon={
            <IconLibrary
              iconName="SignOut"
              size={18}
              weight="regular"
              color="error"
            />
          }
        >
          Leave call
        </Button>
      </FlexContainer>
    </>
  );
};
